import { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { shareReport } from '../../store/reportStore/reportThunk';
import { fetchAccountSettings } from '../../store/accountThunk';

/**
 * Reusable "share report by email" hook for report pages.
 *
 * Wires the Share button on a report to the generic backend share flow: it emails the report
 * (PDF + Excel) to the account's own email plus — when the master "Share reports" toggle is ON —
 * the configured recipients. Also exposes a dynamic tooltip that reflects that toggle state.
 *
 * @param endpoint  report API path, e.g. 'day-book', 'profit-loss', 'balance-sheet',
 *                  'sale-report?voucherCategory=sales', 'tds-payable'
 * @param getParams function returning the current query string (filters) at click time, or '' if none
 */
export const useReportShare = (endpoint: string, getParams: () => string) => {
    const dispatch = useAppDispatch();
    const { selectedAccount } = useAppSelector(state => state.auth);
    const [shareEnabled, setShareEnabled] = useState(false);
    const [sharing, setSharing] = useState(false);

    // Load the master "shareReports" toggle so the tooltip reflects who will receive the report.
    useEffect(() => {
        const id = selectedAccount?._id;
        if (!id) return;
        dispatch(fetchAccountSettings(id))
            .unwrap()
            .then((res: any) => setShareEnabled(!!res?.settings?.emailNotifications?.shareReports))
            .catch(() => { /* keep default false on failure */ });
    }, [dispatch, selectedAccount?._id]);

    const handleShare = useCallback(() => {
        const params = getParams();
        setSharing(true);
        dispatch(shareReport({ endpoint, params }))
            .unwrap()
            .then((data: any) => {
                if (data && data.shared === false) {
                    toast.info(data.message || 'Report not shared.');
                } else if (data && data.shared === true) {
                    toast.success(data.message || 'Report shared successfully');
                }
            })
            .catch((error: any) => {
                toast.error(error?.message || 'Failed to share report');
            })
            .finally(() => setSharing(false));
    }, [dispatch, endpoint, getParams]);

    const shareTooltip = shareEnabled
        ? 'Share Report on Account email + configured recipients'
        : 'Share Report on Account email';

    return { handleShare, shareTooltip, shareEnabled, sharing };
};
